import { useEffect, useId, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router";
import { useAuth } from "../../auth/AuthContext";
import { TierBadge } from "../brand/TierBadge";
import { ROUTES } from "../../lib/constants";
import { useDesktopViewport } from "./useDesktopViewport";
import { RouteOutlet } from "./RouteOutlet";

const navItems = [
  { to: ROUTES.DASHBOARD, label: "Overview", end: true },
  { to: ROUTES.KEYS, label: "API Keys", end: false },
  { to: ROUTES.BILLING, label: "Billing", end: false },
  { to: ROUTES.SUBMISSIONS, label: "Submit Wine", end: false },
  { to: ROUTES.SUPPORT, label: "Support", end: false },
];

export function DashboardLayout() {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isDesktop = useDesktopViewport();
  const sidebarId = useId();
  const sidebarRef = useRef<HTMLElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const restoreFocusRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (isDesktop) {
      restoreFocusRef.current = null;
      setSidebarOpen(false);
      return;
    }

    if (!sidebarOpen) {
      const restoreFocusTarget = restoreFocusRef.current;
      restoreFocusRef.current = null;
      restoreFocusTarget?.focus();
      return;
    }

    closeButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setSidebarOpen(false);
        return;
      }

      if (event.key !== "Tab" || !sidebarRef.current) return;

      const focusables = Array.from(
        sidebarRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element) => !element.hasAttribute("disabled") && element.tabIndex >= 0);

      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isDesktop, sidebarOpen]);

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      navigate(ROUTES.HOME);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      {/* Mobile sidebar toggle */}
      <button
        type="button"
        className="md:hidden mb-4 inline-flex min-h-11 min-w-11 items-center justify-center p-3 text-terroir hover:text-noir"
        onClick={(event) => {
          restoreFocusRef.current = event.currentTarget;
          setSidebarOpen(true);
        }}
        aria-label="Open sidebar"
        aria-controls={sidebarId}
        aria-expanded={sidebarOpen}
      >
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-noir/20 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="md:flex md:gap-8">
        {/* Sidebar */}
        <aside
          id={sidebarId}
          ref={sidebarRef}
          hidden={!isDesktop && !sidebarOpen}
          aria-hidden={!isDesktop && !sidebarOpen}
          className={`
            fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform
            ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
            md:relative md:translate-x-0 md:w-56 md:shrink-0 md:shadow-none md:bg-transparent md:block
          `}
        >
          <div className="bg-white border-[0.5px] border-terroir/20 rounded-lg p-4 sticky top-24 md:mt-0 mt-4 mx-4 md:mx-0">
            {/* Mobile close button */}
            <button
              ref={closeButtonRef}
              type="button"
              className="md:hidden absolute top-3 right-3 inline-flex min-h-11 min-w-11 items-center justify-center p-2 text-terroir hover:text-noir"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close sidebar"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="mb-4 pb-4 border-b-[0.5px] border-terroir/10">
              <p className="text-sm font-medium text-noir truncate">
                {user?.email}
              </p>
              <div className="mt-1">
                <TierBadge tier={user?.tier ?? "free"} />
              </div>
            </div>

            <nav className="space-y-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    `block min-h-11 px-3 py-2.5 rounded-md text-sm transition-colors no-underline ${
                      isActive
                        ? "bg-barrel/10 text-barrel font-medium"
                        : "text-terroir hover:text-noir hover:bg-noir/5"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              {isAdmin && (
                <NavLink
                  to={ROUTES.ADMIN}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    `block min-h-11 px-3 py-2.5 rounded-md text-sm transition-colors no-underline ${
                      isActive
                        ? "bg-barrel/10 text-barrel font-medium"
                        : "text-terroir hover:text-noir hover:bg-noir/5"
                    }`
                  }
                >
                  Admin
                </NavLink>
              )}
            </nav>

            <div className="mt-4 pt-4 border-t-[0.5px] border-terroir/10">
              <button
                type="button"
                onClick={handleLogout}
                className="w-full text-left min-h-11 px-3 py-2.5 rounded-md text-sm text-terroir hover:text-noir hover:bg-noir/5 transition-colors"
              >
                Log out
              </button>
            </div>
          </div>
        </aside>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <RouteOutlet />
        </div>
      </div>
    </div>
  );
}
